import React, { useState } from "react";
import { Link } from "react-router-dom";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

const Footer = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const r = await subscribeNewsletter(email);
            toast.success(
                r.status === "already_subscribed"
                    ? "You're already on the list."
                    : "Locked in. First drop alert incoming."
            );
            setEmail("");
        } catch {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer
            className="border-t-4 border-black bg-black text-white"
            data-testid="footer"
        >
            <div className="px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-start gap-4 md:gap-6">
                        <h3 className="font-heading text-5xl md:text-7xl leading-none">
                            JOIN THE
                            <br />
                            <span className="text-hazard">DROP LIST.</span>
                        </h3>
                        <img
                            src={`${process.env.PUBLIC_URL}/favicon.png`}
                            alt=""
                            aria-hidden="true"
                            className="-ml-1 mt-8 h-16 w-16 md:ml-1 md:mt-10 md:h-24 md:w-24 object-contain flex-shrink-0"
                        />
                    </div>
                    <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.2em] text-white/60">
                        Early access. No spam. Cancel anytime.
                    </p>
                    <form onSubmit={submit} className="mt-6 flex max-w-md">
                        <input
                            type="email"
                            required
                            placeholder="john@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 bg-transparent border-2 border-white px-4 py-3 font-mono-ui text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-hazard"
                            data-testid="newsletter-email-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="border-2 border-l-0 border-white bg-white text-ink px-6 font-mono-ui text-xs uppercase tracking-[0.2em] hover:bg-hazard hover:text-white hover:border-hazard transition-colors disabled:opacity-50"
                            data-testid="newsletter-submit-button"
                        >
                            {loading ? "..." : "Subscribe"}
                        </button>
                    </form>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                        Shop
                    </h4>
                    <ul className="space-y-2 font-mono-ui text-sm">
                        <li>
                            <Link to="/shop/tops" className="hover:text-hazard" data-testid="footer-tops">
                                Tops
                            </Link>
                        </li>
                        <li>
                            <Link to="/shop/bottoms" className="hover:text-hazard" data-testid="footer-bottoms">
                                Bottoms
                            </Link>
                        </li>
                        <li>
                            <Link to="/shop/accessories" className="hover:text-hazard" data-testid="footer-accessories">
                                Accessories
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                        House
                    </h4>
                    <ul className="space-y-2 font-mono-ui text-sm">
                        <li>
                            <Link to="/lookbook" className="hover:text-hazard" data-testid="footer-lookbook">
                                Lookbook
                            </Link>
                        </li>
                        <li>
                            <Link to="/about" className="hover:text-hazard" data-testid="footer-about">
                                About
                            </Link>
                        </li>
                        <li>
                            <Link to="/stockists" className="hover:text-hazard" data-testid="footer-stockists">
                                Locations
                            </Link>
                        </li>
                        <li>
                            <Link to="/press" className="hover:text-hazard" data-testid="footer-press">
                                Press
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <h4 className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                        Service
                    </h4>
                    <ul className="space-y-2 font-mono-ui text-sm">
                        <li>
                            <Link to="/shipping" className="hover:text-hazard" data-testid="footer-shipping">
                                Shipping & Returns
                            </Link>
                        </li>
                        <li>
                            <Link to="/size-guide" className="hover:text-hazard" data-testid="footer-size-guide">
                                Size Guide
                            </Link>
                        </li>
                        <li>
                            <Link to="/care" className="hover:text-hazard" data-testid="footer-care">
                                Care
                            </Link>
                        </li>
                        <li>
                            <Link to="/faq" className="hover:text-hazard" data-testid="footer-faq">
                                FAQ
                            </Link>
                        </li>
                        <li>
                            <Link to="/contact" className="hover:text-hazard" data-testid="footer-contact">
                                Contact
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/20 px-4 md:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-4 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-white/40">
                <span>© 2026 UKnitwear. All Rights Reserved.</span>
                <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                    <Link to="/privacy" className="hover:text-hazard" data-testid="footer-privacy">
                        Privacy
                    </Link>
                    <Link to="/cookies" className="hover:text-hazard" data-testid="footer-cookies">
                        Cookies
                    </Link>
                    <Link to="/terms" className="hover:text-hazard" data-testid="footer-terms">
                        Terms
                    </Link>
                </nav>
                <span>Made in / FOR / The streets.</span>
            </div>
        </footer>
    );
};

export default Footer;
